import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  EventBusService,
  MyHttpClientFactory,
  NetworkService,
  OfflineHttpTrackingService,
  OfflineStorageService,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import {
  IMobileInventoryCount,
  IInventoryEquipmentOption,
  IInventoryDetailUniqueItem,
  IMobileInventoryCountDetailDto,
  JsonPatch,
  JsonPatchOperation,
  JsonPatchWithId,
  IInventoryCount,
  IInventoryCountStatusChangeAction,
  IInventoryItemType,
  ItemListDTO,
  IManufacturer,
} from '../models';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { firstValueFrom, map } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { parseData, storagePrefix } from '@ups/xplat/utils';
import { IMobileInventoryGroup } from '../models/IMobileInventoryGroup';

const _EQUIPMENT_COUNT_LIST_MOBILE = (equipmentId: number) =>
  `/api/mobile/${equipmentId}`;
const _GET_EQUIPMENT_WITH_INVENTORY_TEMPLATE = `api/InventoryTemplate/equipment`;
const _GET_EQUIPMENT_WITH_INVENTORY_ASYNC_TEMPLATE = `api/equipment/async`;
const _ADD_UNIQUE_ITEM = (countDetailId) =>
  `api/mobile/countDetail/uniqueItem/${countDetailId}`;
const _ADD_ITEM = (countId) => `api/Mobile/${countId}/countDetail`;

const _GET_ITEMS_LIST = `/api/Item/list`;
const COUNTS_ENDPOINT = `/api/inventoryCount`;
const COUNT_STATUS_ENDPOINT = (
  countId,
  status: IInventoryCountStatusChangeAction
) => `${COUNTS_ENDPOINT}/${countId}/status/${status}`;
const MANUFACTURERS_ENDPOINT = `api/Item/manufacturers`;

const TIMEOUT = 30000;

@Injectable({
  providedIn: 'root',
})
export class MobileInventoryCountService {
  httpToolTracker: HttpClient;
  inventoryStatusTitle: string;
  selectedEquipment: IInventoryEquipmentOption;
  selectedInventoryCount: IMobileInventoryCount;
  selectedInventoryCountDetail: IMobileInventoryCountDetailDto;
  selectedInventoryCountDetails: IMobileInventoryCountDetailDto[];
  selectedInventoryGroup: IMobileInventoryGroup;
  toolTrackerBaseUrl: string;
  currentMode: string;
  itemList: ItemListDTO[];
  manufacturerList: IManufacturer[];
  filterNotesOnly = false;
  filterQuery = '';

  /* eslint-disable */
  offlinePatches: { [key: string]: any[] } = {};
  /* eslint-enable */
  changesMade = new BehaviorSubject(null);
  slideChange = new BehaviorSubject(null);
  triggerNextSlide = new BehaviorSubject(false);
  private _inventoryGroups: IMobileInventoryGroup[] = [];
  private offlineKey = `${storagePrefix}offlinePutOrPostQueue`;

  constructor(
    httpClientFactory: MyHttpClientFactory,
    private eventBus: EventBusService,
    private networkService: NetworkService,
    private offlineStorage: OfflineStorageService,
    private offlineHttpTracking: OfflineHttpTrackingService,
    private zone: NgZone
  ) {
    this.toolTrackerBaseUrl = environment.urls.toolTrackerAPI;

    this.httpToolTracker = httpClientFactory.createHttpClient(
      this.toolTrackerBaseUrl,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  get inventoryGroups() {
    if (this.currentMode === 'pull') {
      this._inventoryGroups.forEach((group) => {
        group.mode = 'pull';
      });
    } else if (this.currentMode === 'return') {
      this._inventoryGroups.forEach((group) => {
        group.mode = 'return';
      });
    } else {
      if (this._inventoryGroups) {
        this._inventoryGroups.forEach((group) => {
          group.mode = null;
        });
      }
    }
    return this._inventoryGroups;
  }

  set inventoryGroups(groups: IMobileInventoryGroup[]) {
    this._inventoryGroups = groups;
  }

  getItem(refreshItem: IMobileInventoryCountDetailDto) {
    const groupIndex = this._inventoryGroups.findIndex((group) => {
      return group.groupId === refreshItem.itemGroupId;
    });

    const itemIndex = this._inventoryGroups[groupIndex]._items.findIndex(
      (item) => {
        return (
          item.inventoryCountDetailId === refreshItem.inventoryCountDetailId
        );
      }
    );

    return this._inventoryGroups[groupIndex]._items[itemIndex];
  }

  updateItem(updatedItem: IMobileInventoryCountDetailDto) {
    const groupIndex = this._inventoryGroups.findIndex((group) => {
      return group.groupId === updatedItem.itemGroupId;
    });

    this._inventoryGroups[groupIndex].updateItem(updatedItem);
    this.selectedInventoryCountDetail = updatedItem;
    this.changesMade.next(true);
  }

  // TODO: Create a BaseInventoryCountService
  // Move the duplicate methods (InventoryCountService and InventoryCountService) into BaseInventoryCountService
  // Duplicate

  async addUniqueItemToDetail(
    countDetailId: string,
    uniqueItem: IInventoryDetailUniqueItem
  ) {
    const url = _ADD_UNIQUE_ITEM(countDetailId);

    return this.httpToolTracker.post(url, uniqueItem).pipe(timeout(TIMEOUT));
  }

  getInventoryTemplateEquipment(): Observable<IInventoryEquipmentOption[]> {
    return this.httpToolTracker
      .get<IInventoryEquipmentOption[]>(_GET_EQUIPMENT_WITH_INVENTORY_TEMPLATE)
      .pipe(timeout(TIMEOUT));
  }

  getInventoryTemplateEquipmentAsync(): Observable<
    IInventoryEquipmentOption[]
  > {
    return this.httpToolTracker
      .get<
        IInventoryEquipmentOption[]
      >(_GET_EQUIPMENT_WITH_INVENTORY_ASYNC_TEMPLATE)
      .pipe(timeout(TIMEOUT));
  }

  getEquipmentCountListMobileUrl(
    equipmentKeyId: number,
    needBase: boolean = false
  ): string {
    return (
      (needBase ? this.toolTrackerBaseUrl : '') +
      _EQUIPMENT_COUNT_LIST_MOBILE(equipmentKeyId)
    );
  }

  getEquipmentCountListMobile(
    equipmentKeyId: number
  ): Observable<IMobileInventoryCount[]> {
    return this.httpToolTracker
      .get<
        IMobileInventoryCount[]
      >(this.getEquipmentCountListMobileUrl(equipmentKeyId))
      .pipe(timeout(TIMEOUT));
  }

  getEquipmentCountDetails(
    inventoryCountId: string
  ): Observable<IMobileInventoryCountDetailDto[]> {
    return this.httpToolTracker
      .get<
        IMobileInventoryCountDetailDto[]
      >(`/api/mobile/countDetails/${inventoryCountId}`)
      .pipe(timeout(TIMEOUT));
  }

  async patchCountDetail(
    countId: string,
    countDetailId: string,
    itemName: string,
    patches: JsonPatch[]
  ) {
    const url = `/api/InventoryCount/${countId}/${this.selectedInventoryCount.inventoryStatusId}`;

    const patch: JsonPatchWithId = {
      id: countDetailId,
      jsonPatchDocument: patches,
    };

    if (this.networkService.isOffline) {
      if (countDetailId === '00000000-0000-0000-0000-000000000000') {
        for (const p of patches) {
          await this.updateOfflineItem(
            '00000000-0000-0000-0000-000000000000',
            p['path'],
            p['value'],
            false,
            itemName
          );
        }
        return of(true);
      } else {
        const offline = await this.combineOfflinePatches(url, patch);
        if (offline) {
          return of(true);
        }
      }
    }

    return this.httpToolTracker
      .patch(url, [patch])
      .pipe(timeout(TIMEOUT), map(parseData));
  }

  async patchInventoryCountDetailUniqueItem(
    inventoryCountDetailId: string,
    uniqueItemId: string,
    path: string,
    value: number | string | boolean
  ) {
    const url = `/api/InventoryCount/uniqueItem/${inventoryCountDetailId}/${uniqueItemId}/${this.selectedInventoryCount.inventoryStatusId}`;

    const patch: JsonPatch[] = [
      {
        op: JsonPatchOperation.Replace,
        path: `/${path}`,
        value: value,
      },
    ];

    if (this.networkService.isOffline) {
      const update = await this.updateOfflineItem(
        inventoryCountDetailId,
        path,
        value,
        false
      );
      if (update) return of(true);
    }

    return this.httpToolTracker
      .patch(url, patch)
      .pipe(timeout(TIMEOUT), map(parseData));
  }

  async patchUniqueItem(
    inventoryCountDetailId: string,
    uniqueItemId: string,
    path: string,
    value: number | string | boolean
  ) {
    const url = `/api/InventoryCount/uniqueItem/${uniqueItemId}`;

    const patch: JsonPatch[] = [
      {
        op: JsonPatchOperation.Replace,
        path: `/${path}`,
        value: value,
      },
    ];

    if (this.networkService.isOffline) {
      const update = await this.updateOfflineItem(
        inventoryCountDetailId,
        path,
        value,
        true
      );
      if (update) return of(true);
    }

    return this.httpToolTracker
      .patch(url, patch)
      .pipe(timeout(TIMEOUT), map(parseData));
  }

  // Mirrors the web InventoryCountService.patchWarehouseData (see
  // libs/tool-tracker/src/services/inventory-count.service.ts:520). The web flow
  // works against the same endpoint in the same dev environment, so we mirror its
  // shape exactly: a plain Observable return (no async wrapper, no per-request
  // timeout pipe) with the same body shape, and the same string-literal `op`
  // values that the web ships. The async/timeout wrapping the legacy mobile code
  // added on top changed the call-site contract (callers had to `await` twice)
  // without changing the wire payload, so it was pure ceremony.
  patchWarehouseData(
    countId: string,
    warehouseId: number,
    warehouseName: string
  ) {
    const url = `/api/InventoryCount/${countId}`;
    return this.httpToolTracker
      .patch(url, [
        {
          op: 'replace',
          path: '/warehouseId',
          value: warehouseId,
        },
        {
          op: 'replace',
          path: '/warehouseName',
          value: warehouseName,
        },
      ] as const)
      .pipe(map(parseData));
  }

  async getCurrentOfflineItems() {
    return await firstValueFrom(this.offlineStorage.getItem(this.offlineKey));
  }

  async combineOfflinePatches(url, patch) {
    url = this.toolTrackerBaseUrl + url;
    const currentOfflineItems = await this.getCurrentOfflineItems();

    if (currentOfflineItems) {
      for (let i = 0; i < currentOfflineItems.length; i++) {
        // Check for matching URLs
        if (currentOfflineItems[i].url === url) {
          if (currentOfflineItems[i].body.length > 0) {
            // Loop through patches array for this URL
            for (let j = 0; j < currentOfflineItems[i].body.length; j++) {
              // Check for matching countDetailId. Append patching content if so.
              if (currentOfflineItems[i].body[j].id === patch.id) {
                // Append to existing jsonPatchDocument
                currentOfflineItems[i].body[j].jsonPatchDocument.push(
                  ...patch.jsonPatchDocument
                );
                await firstValueFrom(
                  this.offlineStorage.setItem(
                    this.offlineKey,
                    currentOfflineItems
                  )
                );
                return true;
              }
            }

            // Append to PATCH array
            currentOfflineItems[i].body.push(patch);
            await firstValueFrom(
              this.offlineStorage.setItem(this.offlineKey, currentOfflineItems)
            );
            this.offlineHttpTracking.trackedRequests = currentOfflineItems;
            return true;
          }
        }
      }
    }
    return false;
  }

  async updateOfflineItem(
    inventoryCountDetailId: string,
    path,
    value,
    isUniqueItem: boolean,
    itemName?: string
  ) {
    const urlAddUnique = `${this.toolTrackerBaseUrl}/${_ADD_UNIQUE_ITEM(
      inventoryCountDetailId
    )}`;

    const urlAddItem = `${this.toolTrackerBaseUrl}/${_ADD_ITEM(
      this.selectedInventoryCount.inventoryCountId
    )}`;

    path = path.substring(1);

    const currentOfflineItems = await this.getCurrentOfflineItems();
    if (currentOfflineItems) {
      for (let i = 0; i < currentOfflineItems.length; i++) {
        // Check for matching URLs
        if (
          currentOfflineItems[i].url === urlAddUnique ||
          (currentOfflineItems[i].url === urlAddItem &&
            currentOfflineItems[i].body['itemName'] === itemName)
        ) {
          if (isUniqueItem) {
            currentOfflineItems[i].body['uniqueItem'][path] = value;
          } else {
            currentOfflineItems[i].body[path] = value;
          }

          await firstValueFrom(
            this.offlineStorage.setItem(this.offlineKey, currentOfflineItems)
          );
          this.offlineHttpTracking.trackedRequests = currentOfflineItems;
          return true;
        }
      }
    }
    return false;
  }

  async updateUniqueItem(
    index: number,
    field: string,
    value: boolean | string
  ) {
    const update$ = this.patchUniqueItem(
      this.selectedInventoryCountDetail.inventoryCountDetailId,
      this.selectedInventoryCountDetail.inventoryCountDetailUniqueItems[index]
        .uniqueItemId,
      field,
      value
    );

    await firstValueFrom(await update$).catch((error) => {
      console.error(error);
      alert(`There was an error updating the item. \n ${error?.error.detail}`);
    });

    this.selectedInventoryCountDetail.inventoryCountDetailUniqueItems[
      index
    ].uniqueItem[field] = value;
    this.updateItem(this.selectedInventoryCountDetail);
  }

  async updateInventoryCountDetailUniqueItem(
    index: number,
    field: string,
    value: boolean | string
  ) {
    const update$ = this.patchInventoryCountDetailUniqueItem(
      this.selectedInventoryCountDetail.inventoryCountDetailId,
      this.selectedInventoryCountDetail.inventoryCountDetailUniqueItems[index]
        .uniqueItemId,
      field,
      value
    );

    await firstValueFrom(await update$).catch((error) => {
      console.error(error);
      alert(`There was an error updating the item. \n ${error?.error.detail}`);
    });

    this.zone.run(() => {
      this.selectedInventoryCountDetail.inventoryCountDetailUniqueItems[index][
        field
      ] = value;
      this.updateItem(this.selectedInventoryCountDetail);
    });
  }

  changeCountStatus(
    count: IMobileInventoryCount,
    status: IInventoryCountStatusChangeAction
  ): Observable<IInventoryCount> {
    let url = COUNT_STATUS_ENDPOINT(count.inventoryCountId, status);

    if (count.inventoryStatusId) {
      url += `?statusId=${count.inventoryStatusId}`;
    }

    return this.httpToolTracker
      .put<IInventoryCount>(url, count)
      .pipe(timeout(TIMEOUT), map(parseData));
  }

  getItemsList(): Observable<IInventoryItemType[]> {
    return this.httpToolTracker
      .get<IInventoryItemType[]>(_GET_ITEMS_LIST)
      .pipe(timeout(TIMEOUT));
  }

  getManufacturers() {
    return this.httpToolTracker
      .get<IManufacturer[]>(MANUFACTURERS_ENDPOINT)
      .pipe(timeout(TIMEOUT));
  }

  async addItem(item: IMobileInventoryCountDetailDto) {
    const url = _ADD_ITEM(item.inventoryCountId);

    return this.httpToolTracker.post(url, item).pipe(timeout(TIMEOUT));
  }
}
